import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { paths } from "@/routes/path";
import { UserRole } from "@/models/user.model";

// Pages
import { LoginPage } from "@/pages/auth/LoginPage";
import { RegisterPage } from "@/pages/auth/RegisterPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { AdmissionStatusPage } from "@/pages/auth/AdmissionStatusPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { ProgrammesPage } from "@/pages/app/programmes/ProgrammesPage";
import { ProgrammeCreatePage } from "@/pages/app/programmes/ProgrammeCreatePage";
import { ProgrammeEditPage } from "@/pages/app/programmes/ProgrammeEditPage";
import { FacultiesPage } from "@/pages/app/faculties/FacultiesPage";
import { DepartmentsPage } from "@/pages/app/departments/DepartmentsPage";
import { ProgrammeDetailsPage } from "@/pages/app/programmes/ProgrammeDetailsPage";
import { ProgrammeCurriculumsPage } from "@/pages/app/programmes/ProgrammeCurriculumsPage";
import { ProgrammeCohortsPage } from "@/pages/app/programmes/ProgrammeCohortsPage";
import { OptimizedCoursesPage } from "@/pages/app/courses/OptimizedCoursesPage";
import { CourseCreatePage } from "@/pages/app/courses/CourseCreatePage";
import { CourseManagePage } from "@/pages/app/courses/CourseManagePage";
import { CoursePage } from "@/pages/app/courses/CoursePage";
import { CourseContentPage } from "@/pages/app/courses/CourseContentPage";
import { CourseModuleEditorPage } from "@/pages/app/courses/CourseModuleEditorPage";
import { CourseModulesPage } from "@/pages/app/courses/CourseModulesPage";
import { LearnerCoursesPage } from "@/pages/app/courses/LearnerCoursesPage";
import { OptimizedAssignmentsPage } from "@/pages/OptimizedAssignmentsPage";
import { AssignmentDetailsPage } from "@/pages/AssignmentDetailsPage";
import { CertificatesPage } from "@/pages/CertificatesPage";
import { TranscriptPage } from "@/pages/TranscriptPage";
import { AnnouncementsPage } from "@/pages/AnnouncementsPage";
import { AnnouncementDetailsPage } from "@/pages/AnnouncementDetailsPage";
import { DiscussionsPage } from "@/pages/DiscussionsPage";
import { DiscussionDetailsPage } from "@/pages/DiscussionDetailsPage";
import { AnalyticsPage } from "@/pages/AnalyticsPage";
import { QuizManagementPage } from "@/pages/app/quizzes/QuizManagementPage";
import { QuizCreatePage } from "@/pages/app/quizzes/QuizCreatePage";
import { QuizEditPage } from "@/pages/app/quizzes/QuizEditPage";
import { QuizDetailPage } from "@/pages/app/quizzes/QuizDetailPage";
import { QuizAttemptsPage } from "@/pages/app/quizzes/QuizAttemptsPage";
import { QuizAttemptDetailPage } from "@/pages/app/quizzes/QuizAttemptDetailPage";
import { CourseQuizListPage } from "@/pages/app/quizzes/CourseQuizListPage";
import { QuizAttemptTakePage } from "@/pages/app/quizzes/QuizAttemptTakePage";
import { LearnerQuizAttemptDetailPage } from "@/pages/app/quizzes/LearnerQuizAttemptDetailPage";
import LearnerPaymentsPage from "@/pages/app/payments/LearnerPaymentsPage";
import { UsersPage } from "@/pages/UsersPage";
import { UserDetailPage } from "@/pages/UserDetailPage";
import { UserProfilePage } from "@/pages/UserProfilePage";
import { UserProfileEditPage } from "@/pages/UserProfileEditPage";
import { UserEditPage } from "@/pages/UserEditPage";
import { UserLicensesPage } from "@/pages/UserLicensesPage";
import { CourseFeedPage } from "@/pages/CourseFeedPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { NotificationSettingsPage } from "@/pages/NotificationSettingsPage";
import { UploadResourcesPage } from "@/pages/UploadResourcesPage";
import { RoleManagementPage } from "@/pages/RoleManagementPage";
import { StudentManagementPage } from "@/pages/StudentManagementPage";
import { StudentEditPage } from "@/pages/StudentEditPage";
import { MorePage } from "@/pages/MorePage";
import { SubmitAssignmentPage } from "@/pages/SubmitAssignmentPage";
import { CreateAssignmentPage } from "@/pages/CreateAssignmentPage";
import { EditAssignmentPage } from "@/pages/EditAssignmentPage";
import { AssignmentSubmissionsPage } from "@/pages/AssignmentSubmissionsPage";
import { SubmissionDetailPage } from "@/pages/SubmissionDetailPage";
import { ProfileSettingsPage } from "@/pages/ProfileSettingsPage";
import { SystemSettingsPage } from "@/pages/SystemSettingsPage";
import { SecuritySettingsPage } from "@/pages/SecuritySettingsPage";
import { ExportPage } from "@/pages/ExportPage";
import { HelpPage } from "@/pages/HelpPage";
import { SystemStatusPage } from "@/pages/SystemStatusPage";
import { NotFoundPage } from "@/pages/NotFoundPage";

// Layouts
import { MainLayoutWithPasswordCheck } from "@/components/layouts/MainLayoutWithPasswordCheck";
import { AuthLayout } from "@/components/layouts/AuthLayout";
import ApplicationFormPage from "./pages/landing/application/ApplicationFormPage";
import ApplicationsPage from "./pages/app/applications/ApplicationsPage";
import ApplicationViewPage from "./pages/app/applications/ApplicationViewPage";

function AppRoutes() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      {/* <Route path={paths.public.home.absolute} element={<LandingPage />} />
      <Route path={paths.public.programmes.absolute} element={<ProgrammesListPage />} /> */}
      <Route path={paths.public.home.absolute} element={<AuthLayout />}>
        <Route index element={<LoginPage />} />
      </Route>
      <Route path={paths.public.apply.absolute} element={<ApplicationFormPage />} />
      <Route path={paths.auth.root.absolute} element={<AuthLayout />}>
        <Route index element={<Navigate to={paths.auth.login.absolute} replace />} />
        <Route path={paths.auth.login.relative} element={<LoginPage />} />
        <Route path={paths.auth.register.relative} element={<RegisterPage />} />
        <Route
          path={paths.auth.forgotPassword.relative}
          element={<ForgotPasswordPage />}
        />
        <Route
          path={paths.auth.resetPassword.relative}
          element={<ResetPasswordPage />}
        />
        <Route
          path={paths.auth.admissionStatus.relative}
          element={<AdmissionStatusPage />}
        />
      </Route>

      {/* Protected Routes */}
      <Route
        path={paths.app.root.absolute}
        element={
          <ProtectedRoute>
            <MainLayoutWithPasswordCheck />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={<Navigate to={paths.app.dashboard.absolute} replace />}
        />
        <Route
          path={paths.app.dashboard.relative}
          element={<DashboardPage />}
        />
        <Route
          path={paths.app.applications.root.absolute}
          element={<ApplicationsPage />}
        />
        <Route
          path={paths.app.applications.detail.relative}
          element={<ApplicationViewPage />}
        />
        <Route
          path={paths.app.programmes.root.relative}
          element={<ProgrammesPage />}
        />
        <Route
          path={paths.app.programmes.create.relative}
          element={<ProgrammeCreatePage />}
        />
        <Route
          path={paths.app.programmes.edit.relative}
          element={<ProgrammeEditPage />}
        />
        <Route
          path={paths.app.programmes.curriculum.relative}
          element={<ProgrammeCurriculumsPage />}
        />
        <Route
          path={paths.app.programmes.cohorts.relative}
          element={<ProgrammeCohortsPage />}
        />
        <Route
          path={paths.app.programmes.detail.relative}
          element={<ProgrammeDetailsPage />}
        />
        <Route
          path={paths.app.faculties.root.relative}
          element={<FacultiesPage />}
        />
        <Route
          path={paths.app.departments.root.relative}
          element={<DepartmentsPage />}
        />

        <Route
          path={paths.app.courses.root.relative}
          element={
            <ProtectedRoute
              roles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}
              redirectPath={paths.app.courses.learner.absolute}
            >
              <OptimizedCoursesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.courses.learner.relative}
          element={
            <ProtectedRoute
              roles={[UserRole.LEARNER]}
              redirectPath={paths.app.courses.root.absolute}
            >
              <LearnerCoursesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.payments.relative}
          element={
            <ProtectedRoute
              roles={[UserRole.LEARNER]}
              redirectPath={paths.app.dashboard.absolute}
            >
              <LearnerPaymentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.courses.create.relative}
          element={<CourseCreatePage />}
        />
        <Route
          path={paths.app.courses.feed.relative}
          element={<CourseFeedPage />}
        />
        <Route
          path={paths.app.courses.detail.relative}
          element={<CoursePage />}
        />
        <Route
          path={paths.app.courses.manage.relative}
          element={<CourseManagePage />}
        />
        <Route
          path={paths.app.courses.modules.root.relative}
          element={<CourseModulesPage />}
        />
        <Route
          path={paths.app.courses.modules.create.relative}
          element={<CourseModuleEditorPage />}
        />
        <Route
          path={paths.app.courses.modules.detail.relative}
          element={<CourseModuleEditorPage />}
        />
        <Route
          path={paths.app.courses.content.relative}
          element={<CourseContentPage />}
        />
        <Route
          path={paths.app.assignments.root.relative}
          element={<OptimizedAssignmentsPage />}
        />
        <Route
          path={paths.app.assignments.detail.relative}
          element={<AssignmentDetailsPage />}
        />
        <Route
          path={paths.app.assignments.submit.relative}
          element={<SubmitAssignmentPage />}
        />
        <Route
          path={paths.app.assignments.create.relative}
          element={<CreateAssignmentPage />}
        />
        <Route
          path={paths.app.assignments.submissions.list.relative}
          element={
            <ProtectedRoute roles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}>
              <AssignmentSubmissionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.assignments.submissions.detail.relative}
          element={
            <ProtectedRoute roles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}>
              <SubmissionDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.assignments.edit.relative}
          element={<EditAssignmentPage />}
        />
        <Route
          path={paths.app.quizzes.root.relative}
          element={
            <ProtectedRoute roles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}>
              <QuizManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.quizzes.create.relative}
          element={
            <ProtectedRoute roles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}>
              <QuizCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.quizzes.edit.relative}
          element={
            <ProtectedRoute roles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}>
              <QuizEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.quizzes.detail.relative}
          element={
            <ProtectedRoute roles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}>
              <QuizDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.quizzes.attempts.list.relative}
          element={
            <ProtectedRoute roles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}>
              <QuizAttemptsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.quizzes.attempts.detail.relative}
          element={
            <ProtectedRoute roles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}>
              <QuizAttemptDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.certificates.relative}
          element={
            <ProtectedRoute roles={[UserRole.ADMIN]}>
              <CertificatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.transcript.relative}
          element={<TranscriptPage />}
        />
        <Route
          path={paths.app.announcements.root.relative}
          element={<AnnouncementsPage />}
        />
        <Route
          path={paths.app.announcements.detail.relative}
          element={<AnnouncementDetailsPage />}
        />
        <Route
          path={paths.app.discussions.root.relative}
          element={<DiscussionsPage />}
        />
        <Route
          path={paths.app.discussions.detail.relative}
          element={<DiscussionDetailsPage />}
        />
        <Route
          path={paths.app.analytics.relative}
          element={<AnalyticsPage />}
        />
        <Route
          path={paths.app.roleManagement.relative}
          element={<RoleManagementPage />}
        />
        <Route
          path={paths.app.studentManagement.root.relative}
          element={
            <ProtectedRoute
              roles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}
              redirectPath={paths.app.dashboard.absolute}
            >
              <StudentManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.studentManagement.edit.relative}
          element={
            <ProtectedRoute
              roles={[UserRole.ADMIN]}
              redirectPath={paths.app.studentManagement.root.absolute}
            >
              <StudentEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.users.root.relative}
          element={
            <ProtectedRoute
              roles={[UserRole.ADMIN]}
              redirectPath={paths.app.studentManagement.root.absolute}
            >
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.users.detail.relative}
          element={<UserDetailPage />}
        />
        <Route
          path={paths.app.users.edit.relative}
          element={
            <ProtectedRoute
              roles={[UserRole.ADMIN]}
              redirectPath={paths.app.dashboard.absolute}
            >
              <UserEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.users.licenses.relative}
          element={
            <ProtectedRoute
              roles={[UserRole.ADMIN]}
              redirectPath={paths.app.users.root.absolute}
            >
              <UserLicensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.export.relative}
          element={<ExportPage />}
        />
        <Route
          path={paths.app.settings.relative}
          element={<SettingsPage />}
        />
        <Route
          path={paths.app.notificationSettings.relative}
          element={<NotificationSettingsPage />}
        />
        <Route
          path={paths.app.uploadResources.relative}
          element={<UploadResourcesPage />}
        />
        <Route
          path={paths.app.profile.relative}
          element={<UserProfilePage />}
        />
        <Route
          path={paths.app.profileEdit.relative}
          element={<UserProfileEditPage />}
        />
        <Route
          path={paths.app.more.relative}
          element={<MorePage />}
        />
        <Route
          path={paths.app.profileSettings.relative}
          element={<ProfileSettingsPage />}
        />
        <Route
          path={paths.app.systemSettings.relative}
          element={<SystemSettingsPage />}
        />
        <Route
          path={paths.app.securitySettings.relative}
          element={<SecuritySettingsPage />}
        />
        <Route
          path={paths.app.help.relative}
          element={<HelpPage />}
        />
        <Route
          path={paths.app.systemStatus.relative}
          element={<SystemStatusPage />}
        />
        <Route
          path={paths.app.quizzes.courseList.relative}
          element={
            <ProtectedRoute
              roles={[UserRole.ADMIN, UserRole.INSTRUCTOR, UserRole.LEARNER]}
            >
              <CourseQuizListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.quizzes.take.relative}
          element={
            <ProtectedRoute roles={[UserRole.LEARNER]}>
              <QuizAttemptTakePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.app.quizzes.learnerAttemptDetail.relative}
          element={
            <ProtectedRoute roles={[UserRole.LEARNER]}>
              <LearnerQuizAttemptDetailPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch-all */}
      <Route path={paths.catchAll} element={<NotFoundPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <NotificationProvider>
          <AppRoutes />
          <ToastContainer position="top-right" autoClose={5000} />
        </NotificationProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
